import React, { useState, useEffect } from "react";
import { useStore } from "react-redux";
import { Card, ListGroup } from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import CarePlanIcon from "../assets/care-plan-1.png";
import { fetchCarePlanByReference } from "../services/carePlanService";
import { resolveName } from "../utils/messageUtils";

function CarePlanCard({
    title,
    inContext,
    carePlanData,
    fhirClient,
    depth = 6,
}) {
    const store = useStore();
    const [showModal, setShowModal] = useState(false);
    const [modalCarePlanDetails, setModalCarePlanDetails] = useState(null);
    const [selectedCarePlan, setSelectedCarePlan] = useState(carePlanData || null);
    const handleModalClose = () => setShowModal(false);

    if (!title)
        title = resolveName(carePlanData);

    /* Sync CarePlan */
    useEffect(() => {
        setSelectedCarePlan(carePlanData || null);
    }, [carePlanData]);


    /* Open modal */
    const handleModalOpen = async () => {
        if (!selectedCarePlan?.id) {
            console.warn("CarePlanCard opened without valid CarePlan");
            setShowModal(true);
            return;
        }

        try {
            const data = await fetchCarePlanByReference(
                `CarePlan/${selectedCarePlan.id}`,
                fhirClient,
                store
            );
            setModalCarePlanDetails(data);
        } catch (err) {
            console.error("Failed to fetch CarePlan details", err);
            setModalCarePlanDetails(null);
        } finally {
            setShowModal(true);
        }
    };

    /* Renderable fields */
    const fieldRenderers = [
        <ListGroup.Item className={styles.listItem} key="title">
            <strong>Title:</strong> {selectedCarePlan?.title || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="status">
            <strong>Status:</strong> {selectedCarePlan?.status || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="intent">
            <strong>Intent:</strong> {selectedCarePlan?.intent || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="period">
            <strong>Period:</strong>{" "}
            {selectedCarePlan?.period?.start || "?"} –{" "}
            {selectedCarePlan?.period?.end || "?"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="category">
            <strong>Category:</strong>{" "}
            {selectedCarePlan?.category?.[0]?.text ||
                selectedCarePlan?.category?.[0]?.coding?.[0]?.display ||
                "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="careTeam">
            <strong>Care Team:</strong>{" "}
            {selectedCarePlan?.careTeam
                ?.map((ct) => ct.display || ct.reference)
                .join(", ") || "N/A"}
        </ListGroup.Item>,
    ];

    return (
        <>
            <Card className={styles.shrinkCard}>
                <Card.Header
                    className={`d-flex justify-content-between align-items-center ${inContext ? styles.inContextCardHeader : styles.cardHeader
                        }`}
                    onClick={handleModalOpen}
                    style={{ cursor: "pointer" }}
                >
                    {/* LEFT SIDE */}
                    <div className="d-flex align-items-center" style={{ pointerEvents: "none" }}>
                        <img src={CarePlanIcon} alt="CarePlan Icon" className={styles.fhirIcon} />

                        <h6 className="mb-0" style={{ marginLeft: "0.5rem" }}>
                            {title?.length > 75
                                ? `CarePlan: ${title.substring(0, 75)}…`
                                : `CarePlan: ${title}`}
                        </h6>
                    </div>
                </Card.Header>

                {depth > 0 && (
                    <Card.Body>
                        <ListGroup variant="flush">
                            {fieldRenderers.slice(0, depth)}
                        </ListGroup>
                    </Card.Body>
                )}
            </Card>

            <GenericFHIRViewerModal
                show={showModal}
                onClose={handleModalClose}
                resource={modalCarePlanDetails}
                fhirClient={fhirClient}
                icon={CarePlanIcon}
            />
        </>
    );
}

export default CarePlanCard;
